import { apiClientEVENTS, apiClientDB } from '@/config_sessions'

import { useToast } from 'vue-toastification'
const toast = useToast()

let inFlightFetch = null
let controller = null


const authHeader = () => ({
    headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    }
})

const handleAxiosError = (error) => {
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.log('Request canceled by AbortController')
        return
    }

    if (error.response) {
        const { status, data } = error.response
        const errorMessage = data.message || data.error || 'Unknown error'

        if (status === 401 && errorMessage.includes('Token')) {
            console.error('Token expired. Redirecting to login.')
            toast.error('Token expired. Please log in again.')
            // Redirect to login page
            window.location.href = '/login'
        } else {
            console.error('Server Error:', errorMessage)
            toast.error(`Server Error: ${errorMessage}`)
        }
    } else if (error.request) {
        console.error('Network Error:', error.request)
        toast.error('Network Error. Please try again later.')
    } else {
        console.error('Error:', error.message)
        toast.error(`Error: ${error.message}`)
    }
}


// Function to format the date and get the timezone offset
const formatDateWithOffset = (date) => {
    // Convert local machine date to UTC time zone
    const dateString = date.toISOString()
    
    // Get the offset in minutes (e.g., -120 for GMT+0200)
    const offsetMinutes = -date.getTimezoneOffset()
    
    // Convert offset to hours and minutes
    const offsetHours = Math.floor(offsetMinutes / 60)
    const offsetMins = offsetMinutes % 60
    const offsetString = `GMT${offsetHours >= 0 ? '+' : ''}${offsetHours.toString().padStart(2, '0')}${offsetMins.toString().padStart(2, '0')}`

    return { date: dateString, offset: offsetString }
}


// Note: there is no minimum-duration parameter on this endpoint. Both this
// function and the removed `getCurrentCounters` used to send `threshold`, which
// the backend route has never read — the slider that fed it was deleted in the
// portal redesign (e76d265). Short visits are real people and stay in the
// counts; the quick-pass engagement buckets classify them as walk-bys instead.
export const getCurrentCountersShared = async () => {
    if (inFlightFetch) {
        return inFlightFetch  // Return same in-flight promise to all callers
    }

    controller = new AbortController()
    const signal = controller.signal

    inFlightFetch = apiClientEVENTS.get('/get_current_counters/', {
        ...authHeader(),
        signal
    })
        .then(res => res.data)
        .catch(error => {
            if (error.name !== 'AbortError') {
                console.error('Could not fetch current counters:', error)
                handleAxiosError(error)
            }
            return {}
        })
        .finally(() => {
            inFlightFetch = null
            controller = null
        })

    return inFlightFetch
}

export const abortGetCurrentCounters = () => {
    if (controller) {
        controller.abort()
        controller = null
        inFlightFetch = null
    }
}

export const getPastAverages = async () => {
    try {
        const response = await apiClientEVENTS.get(`/get_past_averages/`, {
            params: {
                
            },
            ...authHeader()
        })
            
        return response.data
    } catch (error) {
        console.error('Could not fetch current counters:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getCurrentCountersByFlowId = async (flowId) => {
    try {
        const response = await apiClientEVENTS.get(`/get_current_counters/${flowId}`, {
            params: {
                
            },
            ...authHeader()
        })
            
        return response.data
    } catch (error) {
        console.error('Could not fetch current counters:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getCountersByDuration = async (flowId, duration, opening_hours_mode, options = {}) => {
    
    try {
        const response = await apiClientEVENTS.get(`/get_counters_by_duration/${flowId}`, {
            params: {
                duration: duration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
            
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }
        console.error('Could not fetch counters by duration:', error)
        handleAxiosError(error)
        return {}
    }
}


export async function getCountersByDateRange(flowId, startDate, endDate, opening_hours_mode, options = {}) {
    if (!startDate || !endDate) {
        return {} // Return an empty object when startDate or endDate is null
    }
    const startDateFormatted = formatDateWithOffset(startDate)
    const endDateFormatted = formatDateWithOffset(endDate)

    try {
        const response = await apiClientEVENTS.get(`/get_counters_by_date_range/${flowId}`, {
            params: {
                startDate: startDate,
                endDate: endDate,
                startDateOffset: startDateFormatted.offset, 
                endDateOffset: endDateFormatted.offset,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Could not fetch counters by date range:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getCurrentCountersListByFlowId = async (flowId, dataGranularity) => {
    try {
        const response = await apiClientEVENTS.get(`/get_current_counters_list/${flowId}`, {
            params: {
                dataGranularity: dataGranularity,
            },
            ...authHeader()
        })
            
        return response.data
    } catch (error) {
        console.error('Could not fetch current counters:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getCountersListByDuration = async (flowId, duration, dataGranularity, opening_hours_mode, options = {}) => {
    try {
        const response = await apiClientEVENTS.get(`/get_counters_list_by_duration/${flowId}`, {
            params: {
                duration: duration,
                dataGranularity: dataGranularity,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Could not fetch counters list by day by duration:', error)
        handleAxiosError(error)
        return {}
    }
}


export async function getcountersListByDateRange(flowId, startDate, endDate, dataGranularity, opening_hours_mode, options = {}) {
    if (!startDate || !endDate) {
        return {} // Return an empty object when startDate or endDate is null
    }

    const startDateFormatted = formatDateWithOffset(startDate)
    const endDateFormatted = formatDateWithOffset(endDate)

    try {
        const response = await apiClientEVENTS.get(`/get_counters_list_by_date_range/${flowId}`, {
            params: {
                startDate: startDate,
                endDate: endDate,
                dataGranularity: dataGranularity,
                startDateOffset: startDateFormatted.offset, 
                endDateOffset: endDateFormatted.offset,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Could not fetch counters list by day by date range:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getEventListByDuration = async (flowId, duration, opening_hours_mode, options = {}) => {
    try {
        const response = await apiClientEVENTS.get(`/get_event_list_by_duration/${flowId}`, {
            params: {
                duration: duration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Could not fetch event list by duration:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getEventListByDateRange = async (flowId, startDate, endDate, opening_hours_mode, options = {}) => {
    if (!startDate || !endDate) {
        return {} // Return an empty object when startDate or endDate is null
    }

    const startDateFormatted = formatDateWithOffset(startDate)
    const endDateFormatted = formatDateWithOffset(endDate)

    try {
        const response = await apiClientEVENTS.get(`/get_event_list_by_date_range/${flowId}`, {
            params: {
                startDate: startDate,
                endDate: endDate,
                startDateOffset: startDateFormatted.offset, 
                endDateOffset: endDateFormatted.offset,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Could not fetch event list by date range:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getHeatmapByDuration = async (selectedDuration, opening_hours_mode) => {
    try {
        const response = await apiClientEVENTS.get(`/get_heatmap_by_duration/`, {
            params: {
                selectedDuration: selectedDuration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader()
        })
        return response.data
    } catch (error) {
        console.error('Error fetching data for fixed dashboard widgets:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getEntranceChartDataByDuration = async (selectedDuration, opening_hours_mode) => {
    try {
        const response = await apiClientEVENTS.get(`/get_entrance_chart_data_by_duration/`, {
            params: {
                selectedDuration: selectedDuration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader()
        })
        return response.data
    } catch (error) {
        console.error('Error fetching data for fixed dashboard widgets:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getTopPDDChartData = async (selectedDuration, opening_hours_mode) => {
    try {
        const response = await apiClientEVENTS.get(`/get_top_pdd_chart_data/`, {
            params: {
                selectedDuration: selectedDuration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader()
        })
        return response.data
    } catch (error) {
        console.error('Error fetching data for fixed dashboard widgets:', error)
        handleAxiosError(error)
        return {}
    }
}

export const getFixedDashboardCountersValues = async (selectedDuration, opening_hours_mode, options = {}) => {
    try {
        const response = await apiClientEVENTS.get(`/get_fixed_dashboard_counters_values/`, {
            params: {
                selectedDuration: selectedDuration,
                opening_hours_mode: opening_hours_mode
            },
            ...authHeader(),
            ...options
        })
        return response.data
    } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
            if (import.meta.env.MODE === 'development') {
                console.log('Request canceled by AbortController')
            }
            return {}
        }

        console.error('Error fetching data for fixed dashboard widgets:', error)
        handleAxiosError(error)
        return {}
    }
}

export const getZoneTypeAnalytics = async (
  selectedDuration,
  opening_hours_mode,
  zoneTypes = [5, 999],
  options = {}
) => {
  try {
    const response = await apiClientEVENTS.get('/get_zone_type_analytics/', {
      params: {
        selectedDuration,
        opening_hours_mode,
        // backend expects a comma-separated list, e.g. "5,999"
        zoneTypes: Array.isArray(zoneTypes) ? zoneTypes.join(',') : zoneTypes
      },
      ...authHeader(),
      ...options
    })
    return response.data
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      if (import.meta.env.MODE === 'development') {
        console.log('Request canceled by AbortController')
      }
      return {}
    }

    console.error('Error fetching zone-type analytics:', error)
    handleAxiosError(error)
    return {}
  }
}

export const getDetectionZoneData = async (dateRangeMode, selectedDuration, startDate, endDate, openingHoursMode, flowId, detectionzoneName, detectionZoneTypeId) => {
    let startDateFormatted, endDateFormatted;

    if (dateRangeMode == "dateRange" && startDate && endDate) {
        startDateFormatted = formatDateWithOffset(startDate)
        endDateFormatted = formatDateWithOffset(endDate)
    }

    try {
        const response = await apiClientEVENTS.get(`/get_detection_zone_data/`, {
            params: {
                dateRangeMode: dateRangeMode,
                selectedDuration: selectedDuration,
                startDate: startDate,
                endDate: endDate,
                startDateOffset: startDateFormatted?.offset,
                endDateOffset: endDateFormatted?.offset,
                openingHoursMode: openingHoursMode,
                flowId: flowId,
                detectionzoneName: detectionzoneName,
                detectionZoneTypeId: detectionZoneTypeId,
            },
            ...authHeader()
        })
        return response.data
    } catch (error) {
        console.error('Error fetching detection zone data:', error)
        handleAxiosError(error)
        return {}
    }
}

export const getTotalStoreVisitors = async (dateRangeMode, selectedDuration, startDate, endDate, openingHoursMode, allZones) => {
    let startDateFormatted, endDateFormatted;

    if (dateRangeMode == "dateRange" && startDate && endDate) {
        startDateFormatted = formatDateWithOffset(startDate)
        endDateFormatted = formatDateWithOffset(endDate)
    }

    try {
        const response = await apiClientEVENTS.post(
            `/get_total_store_visitors/`,
            {
              dateRangeMode,
              selectedDuration,
              startDate,
              endDate,
              startDateOffset: startDateFormatted?.offset,
              endDateOffset:   endDateFormatted?.offset,
              openingHoursMode,
              allZones         // pass the array directly
            },
            { ...authHeader() }   // same auth headers, but now as third arg
        )
        return response.data
    } catch (error) {
        console.error('Error fetching total store visitors:', error)
        handleAxiosError(error)
        return {}
    }
}


export const getAllenInsight = async ({ level = 'store', tab = 'data', zone = null, slot = 'past_30_days', signal } = {}) => {
    try {
        const params = { level, slot }
        if (level === 'zone') params.zone = zone
        else params.tab = tab
        const response = await apiClientEVENTS.get(`/allen_insight/`, { ...authHeader(), params, signal })
        return response.data
    }
    catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return {}
        console.error('Error fetching Allen insight:', error)
        handleAxiosError(error)
        return { status: 'error', error: error.message }
    }
}


// Nightly-precomputed weather-response insight. part = 'signature' | 'zones' | 'daily'.
// Returns {status:'unavailable'} when the store has no configured weather location
// (or no run yet) so the caller hides weather.
export const getWeatherInsight = async ({ part = 'signature', signal } = {}) => {
    try {
        const response = await apiClientEVENTS.get(`/weather_insight/`, {
            ...authHeader(), params: { part }, signal,
        })
        return response.data
    }
    catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return {}
        console.error('Error fetching weather insight:', error)
        handleAxiosError(error)
        return { status: 'error', error: error.message }
    }
}


export const getLastUpdatedDataDate = async () => {
    try{
        const response = await apiClientEVENTS.get(`/get_last_updated_data_date/`, {
            params: {
            },
            ...authHeader()
        })
        return response.data

    } catch (error) {
        console.error('Error getting last updated data date', error)
        handleAxiosError(error)
        return {}
    }
}

/**
 * Service response for one zone — how many customers lingered and nobody came.
 * docs/reference/service_response_measurement.md
 *
 * Returns the raw body, which is ALWAYS a state and not always a figure:
 *   { state: 'not_measurable' | 'not_enabled' | 'pending' | 'out_of_scope' }
 *   { state: 'measured', totals, heatmap, stats, pattern, share, settings }
 *
 * Errors resolve to null rather than throwing or toasting: this panel is one part
 * of a page full of other panels, and a zone with no figure yet must not take the
 * rest of the page down or nag the user about it.
 */
export const getServiceResponse = async (zoneId, slot = 'past_30_days') => {
    if (!zoneId) return null
    try {
        const response = await apiClientDB.get(`/service_response/${zoneId}`, {
            params: { slot },
            ...authHeader(),
        })
        return response.data
    } catch (error) {
        console.warn('getServiceResponse failed:', error?.response?.status || error?.message)
        return null
    }
}


/**
 * Store-level service response, grouped by DetectionZoneTypeId — one block per
 * Explore zone-type section. docs/reference/service_response_measurement.md §9
 *
 * Each block carries its own state ('none_enabled' | 'pending' | 'measured'), so a
 * section can correctly show the adoption prompt before any nightly run has happened.
 * Errors resolve to null; the panels render nothing rather than nagging.
 */
export const getServiceResponseStore = async (slot = 'past_30_days') => {
    try {
        const response = await apiClientDB.get('/service_response/store', {
            params: { slot },
            ...authHeader(),
        })
        return response.data?.byType ?? {}
    } catch (error) {
        console.warn('getServiceResponseStore failed:', error?.response?.status || error?.message)
        return null
    }
}


/**
 * Checkout throughput — real transactions per till plus each till's hour-of-day
 * profile. "Real" is the pipeline's own definition (a visit past that zone's
 * short-lingerer cutoff), not a raw visit count.
 *
 * Returns { state: 'pending' } until the nightly run has produced a figure, and
 * resolves errors to null — one panel must not take down a page full of panels.
 */
export const getCheckoutTransactions = async (slot = 'past_30_days', openingHoursMode = '') => {
    try {
        const response = await apiClientDB.get('/checkout_transactions', {
            params: { slot, openingHoursMode },
            ...authHeader(),
        })
        return response.data
    } catch (error) {
        console.warn('getCheckoutTransactions failed:', error?.response?.status || error?.message)
        return null
    }
}

/**
 * Counter unattended — how often somebody stood at a service counter with nobody behind
 * it. Measured at the interaction zone against its linked staff zone, so it answers a
 * different question from the waiting zone next to it: queueing is "you are behind other
 * customers", this is "you are at the counter and nobody is serving".
 *
 * Every counter comes back, including the ones that cannot be measured, each with its
 * state — a store with four counters and one staff zone should see three "needs setup"
 * rows rather than a page that pretends the other three do not exist.
 *
 * Resolves errors to null: one panel must not take down a page full of panels.
 */
export const getCounterUnattendedStore = async (slot = 'past_30_days', openingHoursMode = '') => {
    try {
        const response = await apiClientDB.get('/counter_unattended/store', {
            params: { slot, openingHoursMode },
            ...authHeader(),
        })
        return response.data
    } catch (error) {
        console.warn('getCounterUnattendedStore failed:', error?.response?.status || error?.message)
        return null
    }
}

/**
 * Counter unattended for ONE service counter. Same measurement as the store view,
 * addressed by zone id.
 *
 * The non-measured states resolve from the zone's own configuration, so the answer is
 * right before the nightly job has ever run — which is every store today:
 *   needs_setup    a real counter with no staff zone drawn (the actionable one)
 *   not_measurable tripwire-only, or self-service (a kiosk, a signboard, a locker)
 *   pending        measurable, no figure yet
 */
export const getCounterUnattended = async (zoneId, slot = 'past_30_days', openingHoursMode = '') => {
    try {
        const response = await apiClientDB.get(`/counter_unattended/${zoneId}`, {
            params: { slot, openingHoursMode },
            ...authHeader(),
        })
        return response.data
    } catch (error) {
        console.warn('getCounterUnattended failed:', error?.response?.status || error?.message)
        return null
    }
}

/**
 * WHEN a store's idle checkout hours fall — one sentence under the DEPLOY gauge,
 * attributing the redeployable-payroll euro that gauge already shows.
 *
 * `null` is the normal answer and not a failure: a store staffed to demand has no idle
 * hours to place, and one whose quiet hours are spread evenly has nothing to move. The
 * gauge renders nothing in both cases.
 */
export const getCheckoutIdlePattern = async () => {
    try {
        const response = await apiClientDB.get('/checkout_idle_pattern', { ...authHeader() })
        return response.data?.idleWhen ?? null
    } catch (error) {
        console.warn('getCheckoutIdlePattern failed:', error?.response?.status || error?.message)
        return null
    }
}
